import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class StripeService {
  // Use index signature to avoid TypeScript errors with environment properties
  private apiUrl = (environment as any).apiUrl || '/api';
  private drupalApiUrl = (environment as any).hostUrl || 'http://localhost:4200';

  constructor(private http: HttpClient) { }

  /**
   * Gets the public Stripe API key
   */
  getPublicKey(): Observable<any> {
    return this.http.get(`${this.apiUrl}/stripe-public-key`);
  }

  /**
   * Creates a payment link for subscription
   */
  createPaymentLink(priceId: string, successUrl: string, cancelUrl: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/subscription/payment-link`, {
      priceId,
      successUrl,
      cancelUrl
    });
  }

  /**
   * Creates a Stripe checkout session
   */
  createCheckoutSession(priceId: string, successUrl: string, cancelUrl: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/subscription/create-checkout-session`, {
      priceId,
      successUrl,
      cancelUrl
    });
  }

  /**
   * Retrieves a session by ID
   */
  getSession(sessionId: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/subscription/sessions/${sessionId}`);
  }

  /**
   * Creates a customer portal session for managing subscriptions.
   *
   * The Stripe customer is resolved server-side from the studio, so the
   * customer ID is deliberately not sent by the client.
   */
  createCustomerPortalSession(studioId: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/create-customer-portal`, {
      studio_id: studioId
    });
  }

  /**
   * Updates a customer's payment method
   */
  updatePaymentMethod(customerId: string, paymentMethodId: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/subscription/update-payment-method`, {
      customerId,
      paymentMethodId
    });
  }

  /**
   * Gets a studio's subscription details
   */
  getCustomerSubscription(studioId: string): Observable<any> {
    // Not directly available in current API - we'll adapt with the data from subscription-status
    return this.http.get(`${this.apiUrl}/subscription-status?studio_id=${studioId}`);
  }

  /**
   * Gets a studio's payment history
   */
  getPaymentHistory(studioId: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/payment-history?studio_id=${studioId}`);
  }

  /**
   * Gets a Stripe customer associated with a studio
   */
  getStudioCustomer(studioId: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/subscription-status?studio_id=${studioId}`);
  }

  /**
   * Updates a subscription's collection method (automatic vs. manual)
   */
  updateSubscriptionCollectionMethod(subscriptionId: string, collectionMethod: 'charge_automatically' | 'send_invoice'): Observable<any> {
    return this.http.post(`${this.apiUrl}/update-autopay`, {
      studio_id: subscriptionId, // Using subscription ID as studio ID for now
      enabled: collectionMethod === 'charge_automatically'
    });
  }
}
